import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import baseUrl from "../../../../api/baseUrl";
import { useNavigate } from "react-router-dom";
import BottomTab from "../../../../components/pagination/BottomTab";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import AllPaymentReport from "./AllPaymentReport";
import DrugHistoryModal from "./DrugHistoryModal";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import FormDate from "../../../../components/formFeild/FormDate";
import FormCheck from "../../../../components/formFeild/FormCheck";
import { toast } from "react-toastify";
import CircularProgress from "@mui/material/CircularProgress";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckIcon from "@mui/icons-material/Check";
import {
  formatLinkedAppointmentPurpose,
  formatLinkedAppointmentTime,
  formatLinkedAppointmentToken,
} from "../../../../components/LinkedAppointmentChip";
import { browserTimeZone, toLocalYmd, todayLocalYmd } from "../../../../utils/colomboDate";
import {
  DATE_PRESET_OPTIONS,
  INVOICE_LIST_PAGE_SIZE,
  dateTriggerLabel,
  isSummaryDateMode,
  rangeForDateMode,
  type DateRangeMode,
} from "./dateRangePresets";

interface FormData {
  payments: boolean;
  startDate: string;
  endDate: string;
}

function listRowDate(item: {
  periodLastPostedAt?: string;
  updatedAt?: string;
  paymentDate?: string;
  businessDate?: string;
}): string {
  return (
    toLocalYmd(
      item?.periodLastPostedAt ||
        item?.paymentDate ||
        item?.businessDate ||
        item?.updatedAt
    ) || "—"
  );
}

function toAmount(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatAmount(value: unknown): string {
  return toAmount(value).toLocaleString();
}

type DentistOption = {
  _id: string;
  title?: string;
  lastName?: string;
};

function dentistLabel(d: DentistOption): string {
  return [d.title, d.lastName].filter(Boolean).join(" ").trim() || "Dentist";
}

function getPaidTotal(item: {
  cash?: unknown;
  card?: unknown;
  bankTransfer?: unknown;
}) {
  return (
    toAmount(item?.cash) + toAmount(item?.card) + toAmount(item?.bankTransfer)
  );
}

/** Prefer API aggregates; fall back to summing paymentInfo rows. */
function resolveInvoiceAmounts(item: any) {
  const hasAggregates =
    item?.cash != null ||
    item?.card != null ||
    item?.bankTransfer != null ||
    item?.discount != null;

  if (hasAggregates) {
    const cash = toAmount(item?.cash);
    const card = toAmount(item?.card);
    const bankTransfer = toAmount(item?.bankTransfer);
    const discount = toAmount(item?.discount);
    const paid = cash + card + bankTransfer;
    const due =
      item?.due != null
        ? toAmount(item.due)
        : toAmount(item?.totalPrice) - paid - discount;
    return { cash, card, bankTransfer, discount, paid, due };
  }

  const totals = (item?.paymentInfo ?? []).reduce(
    (
      acc: {
        card: number;
        cash: number;
        bankTransfer: number;
        discount: number;
      },
      curr: any
    ) => ({
      card: acc.card + toAmount(curr?.card),
      cash: acc.cash + toAmount(curr?.cash),
      bankTransfer: acc.bankTransfer + toAmount(curr?.bankTransfer),
      discount: acc.discount + toAmount(curr?.discount),
    }),
    { card: 0, cash: 0, bankTransfer: 0, discount: 0 }
  );
  const paid = totals.card + totals.cash + totals.bankTransfer;
  const due = toAmount(item?.totalPrice) - paid - totals.discount;
  return { ...totals, paid, due };
}

function dentistRowLabel(item: any): string {
  const names = Array.isArray(item?.dentistNames)
    ? item.dentistNames
        .map((name: unknown) => String(name ?? "").trim())
        .filter(Boolean)
    : [];
  if (names.length) return names.join(", ");
  return (
    [item?.dentistName, item?.dentistLastName].filter(Boolean).join(" ").trim() ||
    "—"
  );
}

function patientRegNo(item: any): string {
  return String(item?.regNo ?? "").trim();
}

function patientPhone(item: any): string {
  return String(item?.mobileNo ?? item?.phone ?? item?.smobileNo ?? "").trim();
}

function todayDateString(): string {
  return todayLocalYmd();
}

type BalanceStatus = "all" | "due" | "advanced" | "complete";

const BALANCE_STATUS_OPTIONS: { value: BalanceStatus; label: string }[] = [
  { value: "all", label: "All" },
  { value: "due", label: "Due" },
  { value: "advanced", label: "Advanced" },
  { value: "complete", label: "Complete" },
];

function balanceStatusLabel(value: BalanceStatus): string {
  return BALANCE_STATUS_OPTIONS.find((o) => o.value === value)?.label ?? "All";
}

function weekdayFromIso(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso).trim());
  const d = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date();
  return [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ][d.getDay()];
}

function mapSessionOptions(raw: unknown): {
  sessionName: string;
  startTime?: string;
  endTime?: string;
}[] {
  if (!Array.isArray(raw)) return [];
  const byName = new Map<
    string,
    { sessionName: string; startTime?: string; endTime?: string }
  >();
  for (const item of raw) {
    const o = item as Record<string, unknown>;
    const sessionName = String(o?.sessionName ?? "").trim();
    if (!sessionName) continue;
    const key = sessionName.toLowerCase();
    if (byName.has(key)) continue;
    byName.set(key, {
      sessionName,
      startTime: String(o?.startTime ?? "").trim(),
      endTime: String(o?.endTime ?? "").trim(),
    });
  }
  return Array.from(byName.values());
}

function parseClockMinutes(raw?: string): number {
  const s = String(raw ?? "").trim();
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return NaN;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return NaN;
  return h * 60 + min;
}

function colomboClockMinutes(iso?: string): number {
  if (!iso) return NaN;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return NaN;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Colombo",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const hour = Number(parts.find((p) => p.type === "hour")?.value);
  const minute = Number(parts.find((p) => p.type === "minute")?.value);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return NaN;
  return hour * 60 + minute;
}

function inSessionWindow(mins: number, startM: number, endM: number): boolean {
  if (!Number.isFinite(mins)) return false;
  if (startM <= endM) return mins >= startM && mins < endM;
  return mins >= startM || mins < endM;
}

function currentSessionName(
  list: { sessionName: string; startTime?: string; endTime?: string }[]
): string | null {
  const nowM = colomboClockMinutes(new Date().toISOString());
  if (!Number.isFinite(nowM)) return null;
  const hit = list.find((s) => {
    const startM = parseClockMinutes(s.startTime);
    const endM = parseClockMinutes(s.endTime);
    if (!Number.isFinite(startM) || !Number.isFinite(endM) || startM === endM) {
      return false;
    }
    return inSessionWindow(nowM, startM, endM);
  });
  return hit?.sessionName ?? null;
}

function invoiceMatchesStatus(item: any, status: BalanceStatus): boolean {
  if (status === "all") return true;
  const due = resolveInvoiceAmounts(item).due;
  if (status === "due") return due > 0;
  if (status === "advanced") return due < 0;
  return Math.abs(due) < 0.01;
}

const actionBtnClass =
  "inline-flex h-7 w-7 items-center justify-center rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 dark:focus:ring-offset-slate-800";

function PrescriptionButton({
  onClick,
  className = "",
}: {
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      title="Prescription / drug history"
      aria-label="Prescription / drug history"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`${actionBtnClass} shrink-0 bg-teal-50 text-teal-600 hover:bg-teal-100 focus:ring-teal-500 dark:bg-teal-900/40 dark:text-teal-300 dark:hover:bg-teal-800/50 ${className}`}
    >
      <MedicationOutlinedIcon sx={{ fontSize: 15 }} />
    </button>
  );
}

function InvoiceRowActions({
  treatmentId,
  onViewTreatment,
  onViewInvoice,
}: {
  treatmentId?: string;
  onViewTreatment: (id: string) => void;
  onViewInvoice: (id: string) => void;
}) {
  if (!treatmentId) return null;

  return (
    <div className="flex items-center justify-end gap-1">
      <button
        type="button"
        title="View treatment"
        aria-label="View treatment"
        onClick={(e) => {
          e.stopPropagation();
          onViewTreatment(treatmentId);
        }}
        className={`${actionBtnClass} bg-violet-50 text-violet-600 hover:bg-violet-100 focus:ring-violet-500 dark:bg-violet-900/40 dark:text-violet-300 dark:hover:bg-violet-800/50`}
      >
        <MedicalServicesOutlinedIcon sx={{ fontSize: 15 }} />
      </button>
      <button
        type="button"
        title="View invoice"
        aria-label="View invoice"
        onClick={(e) => {
          e.stopPropagation();
          onViewInvoice(treatmentId);
        }}
        className={`${actionBtnClass} bg-indigo-50 text-indigo-600 hover:bg-indigo-100 focus:ring-indigo-500 dark:bg-indigo-900/40 dark:text-indigo-300 dark:hover:bg-indigo-800/50`}
      >
        <ReceiptLongOutlinedIcon sx={{ fontSize: 15 }} />
      </button>
    </div>
  );
}

function AllLastPayments() {
  const navigate = useNavigate();
  const [report, setreport] = useState(false);
  const [openpayments, setopenpayments] = useState(false);
  const [payment, setpayment] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filterDoctorId, setFilterDoctorId] = useState<string>("all");
  const [dentists, setDentists] = useState<DentistOption[]>([]);
  const [filterSession, setFilterSession] = useState<string>("all");
  const [filterBalance, setFilterBalance] = useState<BalanceStatus>("all");
  const [sessions, setSessions] = useState<
    { sessionName: string; startTime?: string; endTime?: string }[]
  >([]);
  const [drugModal, setDrugModal] = useState<{
    treatmentId: string;
    patientName?: string;
    invoiceNo?: string;
  } | null>(null);
  const [dateRangeMode, setDateRangeMode] = useState<DateRangeMode>("today");
  const [dateMenuOpen, setDateMenuOpen] = useState(false);
  const dateMenuRef = useRef<HTMLDivElement | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const fetchSeqRef = useRef(0);
  const sessionTouchedRef = useRef(false);
  const search = useSelector((state: RootState) => state.searching.patientData);
  const methods = useForm<FormData>({
    defaultValues: {
      payments: false,
      startDate: todayDateString(),
      endDate: todayDateString(),
    },
  });
  const { watch, reset, setValue } = methods;
  const startDate = watch("startDate");
  const endDate = watch("endDate");

  useEffect(() => {
    baseUrl
      .get("/appointment/findAllAppointmentDentist")
      .then((response) => {
        const list = Array.isArray(response.data?.data) ? response.data.data : [];
        setDentists(
          list
            .map((d: DentistOption) => ({
              _id: String(d?._id ?? ""),
              title: d?.title,
              lastName: d?.lastName,
            }))
            .filter((d: DentistOption) => d._id)
        );
      })
      .catch((error) => {
        console.error(error);
        setDentists([]);
      });
  }, []);

  useEffect(() => {
    const date =
      startDate && /^\d{4}-\d{2}-\d{2}$/.test(startDate)
        ? startDate
        : todayDateString();
    const weekday = weekdayFromIso(date);
    let cancelled = false;

    const apply = (
      list: { sessionName: string; startTime?: string; endTime?: string }[]
    ) => {
      if (cancelled) return;
      setSessions(list);
      const fallback = currentSessionName(list) ?? "all";
      setFilterSession((prev) => {
        if (sessionTouchedRef.current) {
          return prev === "all" || list.some((s) => s.sessionName === prev)
            ? prev
            : fallback;
        }
        return fallback;
      });
    };

    baseUrl
      .get("/client/setting/openHours/findSessionNames", {
        params: { date },
      })
      .then((res) => {
        const list = mapSessionOptions(res.data?.data?.sessions);
        if (list.length) {
          apply(list);
          return null;
        }
        return baseUrl.get("/client/setting/generalOpenHours/findGeneralOpenHours", {
          params: { day: weekday },
        });
      })
      .then((res) => {
        if (!res || cancelled) return;
        apply(mapSessionOptions(res.data?.data?.sessions));
      })
      .catch(() => {
        if (cancelled) return;
        baseUrl
          .get("/client/setting/generalOpenHours/findGeneralOpenHours", {
            params: { day: weekday },
          })
          .then((res) => apply(mapSessionOptions(res.data?.data?.sessions)))
          .catch(() => apply([]));
      });

    return () => {
      cancelled = true;
    };
  }, [startDate]);

  const getData = useCallback(
    (
      keyword: string,
      pageNum: number,
      startDate?: string,
      endDate?: string,
      doctorId?: string,
      sessionName?: string,
      options?: { disablePagination?: boolean; balanceStatus?: string }
    ) => {
      const seq = ++fetchSeqRef.current;
      setListLoading(true);
      baseUrl
        .get("payment/getAllPayment", {
          headers: {
            keyword,
            page: options?.disablePagination ? 1 : pageNum,
            ...(options?.disablePagination
              ? { limit: "all" }
              : { limit: INVOICE_LIST_PAGE_SIZE }),
          },
          params: {
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            doctorId:
              doctorId && doctorId !== "all" ? doctorId : undefined,
            sessionName:
              sessionName && sessionName !== "all" ? sessionName : undefined,
            balanceStatus:
              options?.balanceStatus && options.balanceStatus !== "all"
                ? options.balanceStatus
                : undefined,
          },
        })
        .then((response) => {
          if (seq !== fetchSeqRef.current) return;
          setpayment(response.data.data);
          setCount(response.data.count);
        })
        .catch((error) => {
          if (seq !== fetchSeqRef.current) return;
          console.error(error);
        })
        .finally(() => {
          if (seq !== fetchSeqRef.current) return;
          setListLoading(false);
        });
    },
    []
  );

  const fetchPayments = useCallback(
    (
      keyword: string,
      pageNum: number,
      doctorId: string,
      from?: string,
      to?: string,
      sessionName?: string,
      balanceStatus?: string,
      disablePagination?: boolean
    ) => {
      getData(keyword, pageNum, from, to, doctorId, sessionName, {
        disablePagination,
        balanceStatus,
      });
    },
    [getData]
  );

  useEffect(() => {
    const activeSearchTerm = searchTerm || search || "";
    const from = startDate && endDate ? startDate : undefined;
    const to = startDate && endDate ? endDate : undefined;
    // Today / Yesterday: full list + footer totals. Other presets paginate.
    const loadAllRows = isSummaryDateMode(dateRangeMode);
    fetchPayments(
      activeSearchTerm,
      loadAllRows ? 1 : page,
      filterDoctorId,
      from,
      to,
      filterSession,
      filterBalance,
      loadAllRows
    );
  }, [
    search,
    searchTerm,
    page,
    filterDoctorId,
    filterSession,
    filterBalance,
    dateRangeMode,
    startDate,
    endDate,
    fetchPayments,
  ]);

  useEffect(() => {
    if (!dateMenuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!dateMenuRef.current?.contains(e.target as Node)) {
        setDateMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDateMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [dateMenuOpen]);

  useEffect(() => {
    const { unsubscribe } = watch((value) => {
      if (value.payments) {
        setopenpayments(true);
      } else {
        setopenpayments(false);
      }
    });
    return () => unsubscribe();
  }, [watch]);

  const onPageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const closeModal = useCallback(() => {
    setreport(false);
  }, []);

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data) => {
      const hasRange = Boolean(data.startDate && data.endDate);
      if (dateRangeMode !== "all" && !hasRange) {
        toast.info("Select both From and To dates to download the report.");
        return;
      }
      const keyword = (searchTerm || search || "").trim();
      try {
        const response = await baseUrl.get("client/payment/paymentReport", {
          responseType: "blob",
          params: {
            ...(hasRange
              ? { startDate: data.startDate, endDate: data.endDate }
              : {}),
            payments: data.payments,
            timeZone: browserTimeZone(),
            ...(filterDoctorId !== "all" ? { doctorId: filterDoctorId } : {}),
            ...(filterSession !== "all" ? { sessionName: filterSession } : {}),
            ...(filterBalance !== "all" ? { balanceStatus: filterBalance } : {}),
            ...(keyword ? { keyword } : {}),
          },
        });
        if (response.status === 204) {
          toast.info("No data found for the selected filters.");
          return;
        }
        const pdfBlob = new Blob([response.data], { type: "application/pdf" });
        const url = URL.createObjectURL(pdfBlob);
        const fileName = hasRange
          ? `payment-report-${data.startDate}-to-${data.endDate}.pdf`
          : "payment-report-all.pdf";

        const preview = window.open(url, "_blank");
        if (!preview) {
          toast.warn("Allow pop-ups to preview the report, or use Download.");
        }

        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        toast.success("Payment report generated.");
      } catch (error: unknown) {
        const err = error as { response?: { status?: number } };
        if (err?.response?.status === 204) {
          toast.info("No data found for the selected filters.");
          return;
        }
        toast.error("Could not generate the payment report.");
        console.error("Error downloading the PDF", error);
      }
    },
    [dateRangeMode, filterDoctorId, filterSession, filterBalance, search, searchTerm]
  );

  const methodReset = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const todayStr = todayDateString();
      reset({
        payments: false,
        startDate: todayStr,
        endDate: todayStr,
      });
      setSearchTerm("");
      setFilterDoctorId("all");
      sessionTouchedRef.current = false;
      setFilterSession(currentSessionName(sessions) ?? "all");
      setFilterBalance("all");
      setDateRangeMode("today");
      setDateMenuOpen(false);
      setPage(1);
    },
    [reset, sessions]
  );

  const applyDateRangeMode = useCallback(
    (mode: DateRangeMode) => {
      setDateRangeMode(mode);
      setPage(1);
      setDateMenuOpen(false);
      const { start, end } = rangeForDateMode(
        mode,
        startDate,
        endDate,
        todayLocalYmd()
      );
      setValue("startDate", start, { shouldDirty: true, shouldValidate: true });
      setValue("endDate", end, { shouldDirty: true, shouldValidate: true });
    },
    [setValue, startDate, endDate]
  );

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
  }, []);

  const handleDoctorFilter = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setFilterDoctorId(e.target.value);
      setPage(1);
    },
    []
  );

  const handleSessionFilter = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      sessionTouchedRef.current = true;
      setFilterSession(e.target.value);
      setPage(1);
    },
    []
  );

  const handleBalanceFilter = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setFilterBalance(e.target.value as BalanceStatus);
      setPage(1);
    },
    []
  );

  const openTreatment = useCallback(
    (treatmentId: string) => {
      navigate(
        `/client/treatment-diagnose/add-treatment/treatment-table/${treatmentId}`
      );
    },
    [navigate]
  );

  const openInvoice = useCallback(
    (treatmentId: string) => {
      navigate(`/client/payments/invoice/${treatmentId}`);
    },
    [navigate]
  );

  const openDrugHistory = useCallback((item: {
    _id?: string;
    fullName?: string;
    invoiceNo?: string;
  }) => {
    if (!item?._id) return;
    setDrugModal({
      treatmentId: item._id,
      patientName: item.fullName,
      invoiceNo: item.invoiceNo,
    });
  }, []);

  const closeDrugModal = useCallback(() => {
    setDrugModal(null);
  }, []);

  const fieldLabelClass =
    "text-slate-700 dark:text-slate-300 text-[13px] font-medium leading-[1.4]";
  const cellClass = "px-2.5 py-1.5 text-xs";

  const displayedPayments = useMemo(() => {
    let rows = Array.isArray(payment) ? payment : [];
    // Session is applied on the server (appointment same-day match OR clock
    // window). Do not re-filter by createdAt/updatedAt clock here — that drops
    // invoices that matched Morning via appointment but were billed outside
    // the session hours, which under-counts vs All Sessions.
    if (filterBalance !== "all") {
      rows = rows.filter((item) => invoiceMatchesStatus(item, filterBalance));
    }
    return rows;
  }, [payment, filterBalance]);

  const filtersLoadAllRows = isSummaryDateMode(dateRangeMode);
  // When paginated, show API total; Today/Yesterday show filtered in-memory rows.
  const displayedCount = filtersLoadAllRows
    ? displayedPayments.length
    : count;

  const todaySummary = useMemo(() => {
    if (!isSummaryDateMode(dateRangeMode) || displayedPayments.length === 0) {
      return null;
    }

    return displayedPayments.reduce(
      (acc, item: any) => {
        const amounts = resolveInvoiceAmounts(item);
        return {
          totalPrice: acc.totalPrice + toAmount(item?.totalPrice),
          discount: acc.discount + amounts.discount,
          cash: acc.cash + amounts.cash,
          card: acc.card + amounts.card,
          bankTransfer: acc.bankTransfer + amounts.bankTransfer,
          paid: acc.paid + amounts.paid,
          count: acc.count + 1,
        };
      },
      {
        totalPrice: 0,
        discount: 0,
        cash: 0,
        card: 0,
        bankTransfer: 0,
        paid: 0,
        count: 0,
      }
    );
  }, [dateRangeMode, displayedPayments]);

  const summaryTitle =
    dateRangeMode === "yesterday" ? "Yesterday summary" : "Today summary";
  const dateFilterLabel = dateTriggerLabel(dateRangeMode, startDate, endDate);


  const showDentistColumn = filterDoctorId === "all";
  const showTokenColumn = displayedPayments.some((item: any) =>
    Boolean(formatLinkedAppointmentToken(item?.linkedAppointment))
  );
  const showApptTime = displayedPayments.some((item: any) =>
    Boolean(formatLinkedAppointmentTime(item?.linkedAppointment))
  );
  const showPurposeColumn = displayedPayments.some((item: any) =>
    Boolean(formatLinkedAppointmentPurpose(item?.linkedAppointment))
  );
  const leadingColCount =
    1 +
    (showTokenColumn ? 1 : 0) +
    3 +
    (showPurposeColumn ? 1 : 0) +
    (showDentistColumn ? 1 : 0);
  const tableColCount = leadingColCount + 7;
  const summaryLabelColSpan = leadingColCount;

  return (
    <div className="space-y-2.5">
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="flex flex-col gap-2.5 lg:flex-row lg:items-end lg:justify-between"
        >
          <div className="flex flex-1 flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-end">
            <div className="flex w-full flex-col gap-1 sm:max-w-md">
              <label className={fieldLabelClass}>Search</label>
              <input
                type="search"
                placeholder="Invoice, patient, reg no, dentist..."
                value={searchTerm}
                onChange={handleSearch}
                className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 outline-none transition-all placeholder:text-slate-400 focus:border-primary focus:ring-1 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:placeholder:text-slate-500"
              />
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex w-36 shrink-0 flex-col gap-1">
                <label className={fieldLabelClass}>Doctor</label>
                <select
                  value={filterDoctorId}
                  onChange={handleDoctorFilter}
                  className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 outline-none transition-all focus:border-primary focus:ring-1 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                >
                  <option value="all">All</option>
                  {dentists.map((d) => (
                    <option key={d._id} value={d._id}>
                      {dentistLabel(d)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex w-40 shrink-0 flex-col gap-1">
                <label className={fieldLabelClass}>Session</label>
                <select
                  value={filterSession}
                  onChange={handleSessionFilter}
                  className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 outline-none transition-all focus:border-primary focus:ring-1 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                >
                  <option value="all">All Sessions</option>
                  {sessions.map((s) => (
                    <option key={s.sessionName} value={s.sessionName}>
                      {s.sessionName}
                      {s.startTime && s.endTime
                        ? ` (${s.startTime}-${s.endTime})`
                        : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex w-36 shrink-0 flex-col gap-1">
                <label className={fieldLabelClass}>Status</label>
                <select
                  value={filterBalance}
                  onChange={handleBalanceFilter}
                  className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 outline-none transition-all focus:border-primary focus:ring-1 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                >
                  {BALANCE_STATUS_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              {dateRangeMode === "custom" ? (
                <>
                  <FormDate label="From" name="startDate" sameSize />
                  <FormDate label="To" name="endDate" sameSize />
                </>
              ) : null}
              <div className="relative flex w-52 shrink-0 flex-col gap-1" ref={dateMenuRef}>
                <span className={fieldLabelClass}>Date</span>
                <button
                  type="button"
                  disabled={listLoading}
                  onClick={() => setDateMenuOpen((open) => !open)}
                  className="flex h-8 w-full items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-left text-xs text-slate-700 outline-none transition-all hover:border-slate-300 focus:border-primary focus:ring-1 focus:ring-primary/20 disabled:cursor-wait dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-slate-500"
                  aria-haspopup="listbox"
                  aria-expanded={dateMenuOpen}
                >
                  {listLoading ? (
                    <CircularProgress size={12} thickness={5} color="inherit" />
                  ) : (
                    <CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} className="shrink-0 text-slate-400" />
                  )}
                  <span className="min-w-0 flex-1 truncate font-medium">
                    {dateFilterLabel}
                  </span>
                </button>
                {dateMenuOpen ? (
                  <div
                    role="listbox"
                    className="absolute left-0 top-[calc(100%+4px)] z-30 w-56 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-600 dark:bg-slate-800"
                  >
                    {DATE_PRESET_OPTIONS.map(({ value, label }) => {
                      const selected = dateRangeMode === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          disabled={listLoading}
                          onClick={() => applyDateRangeMode(value)}
                          className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors disabled:cursor-wait ${
                            selected
                              ? "bg-indigo-50 font-semibold text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300"
                              : "text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700/80"
                          }`}
                        >
                          <span className="flex-1">{label}</span>
                          {selected ? (
                            <CheckIcon sx={{ fontSize: 16 }} className="text-indigo-600 dark:text-indigo-300" />
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              <div className="flex h-8 items-center">
                <FormCheck label="Payment Breakdown" name="payments" />
              </div>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            <button
              type="submit"
              className="flex h-8 items-center whitespace-nowrap rounded-lg bg-gradient-to-r from-primary to-primaryLight px-3 text-xs font-medium text-white transition-all hover:shadow-md hover:shadow-primary/20"
            >
              Download PDF
            </button>
            <button
              type="button"
              onClick={methodReset}
              className="flex h-8 items-center whitespace-nowrap rounded-lg border border-slate-200 bg-slate-100 px-3 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
            >
              Reset
            </button>
          </div>
        </form>
      </FormProvider>

      <div className="flex flex-wrap items-center justify-between gap-2 px-0.5">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          <span className="font-semibold tabular-nums text-slate-800 dark:text-slate-100">
            {displayedCount}
          </span>{" "}
          {displayedCount === 1 ? "record" : "records"}
          {dateRangeMode === "all" ? (
            <span className="text-slate-400 dark:text-slate-500"> · All Time</span>
          ) : startDate && endDate ? (
            <span className="text-slate-400 dark:text-slate-500">
              {" "}
              · {dateFilterLabel}
            </span>
          ) : null}
          {filterDoctorId !== "all" ? (
            <span className="text-slate-400 dark:text-slate-500"> · Doctor filtered</span>
          ) : null}
          {filterSession !== "all" ? (
            <span className="text-slate-400 dark:text-slate-500">
              {" "}
              · {filterSession}
            </span>
          ) : null}
          {filterBalance !== "all" ? (
            <span className="text-slate-400 dark:text-slate-500">
              {" "}
              · {balanceStatusLabel(filterBalance)}
            </span>
          ) : null}
          {listLoading ? (
            <span className="ml-1 inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-300">
              <CircularProgress size={11} thickness={5} color="inherit" />
              Updating…
            </span>
          ) : null}
        </p>
      </div>

      {/* Desktop Table */}
      <div
        className={`relative hidden overflow-x-auto rounded-lg border border-slate-200 transition-opacity duration-200 dark:border-slate-600 md:block ${
          listLoading ? "pointer-events-none opacity-55" : "opacity-100"
        }`}
      >
        {listLoading ? (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/40 dark:bg-slate-900/30">
            <div className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-xs font-medium text-indigo-700 shadow-sm dark:bg-slate-800 dark:text-indigo-300">
              <CircularProgress size={16} thickness={5} color="inherit" />
              Loading invoices…
            </div>
          </div>
        ) : null}
        <table
          className={`w-full table-fixed ${
            showDentistColumn ? "min-w-[1280px]" : "min-w-[1160px]"
          }`}
        >
          <colgroup>
            <col style={{ width: "108px" }} />
            {showTokenColumn ? <col style={{ width: "72px" }} /> : null}
            <col style={{ width: "72px" }} />
            <col />
            <col style={{ width: "100px" }} />
            {showPurposeColumn ? <col style={{ width: "110px" }} /> : null}
            {showDentistColumn ? <col /> : null}
            <col style={{ width: "72px" }} />
            <col style={{ width: "68px" }} />
            <col style={{ width: "68px" }} />
            <col style={{ width: "68px" }} />
            <col style={{ width: "68px" }} />
            <col style={{ width: "72px" }} />
            <col style={{ width: "108px" }} />
          </colgroup>
          <thead className="border-b border-slate-200 bg-slate-50/90 dark:border-slate-600 dark:bg-slate-700/60">
            <tr>
              <th className={`${cellClass} text-left font-semibold text-slate-500 dark:text-slate-400`}>
                {showApptTime ? "Date / Time" : "Date"}
              </th>
              {showTokenColumn ? (
                <th className={`${cellClass} text-center font-semibold text-slate-500 dark:text-slate-400`}>
                  Token
                </th>
              ) : null}
              <th className={`${cellClass} text-left font-semibold text-slate-500 dark:text-slate-400`}>
                Reg No
              </th>
              <th className={`${cellClass} text-left font-semibold text-slate-500 dark:text-slate-400`}>
                Patient
              </th>
              <th className={`${cellClass} text-left font-semibold text-slate-500 dark:text-slate-400`}>
                Phone
              </th>
              {showPurposeColumn ? (
                <th className={`${cellClass} text-left font-semibold text-slate-500 dark:text-slate-400`}>
                  Purpose
                </th>
              ) : null}
              {showDentistColumn ? (
                <th className={`${cellClass} text-left font-semibold text-slate-500 dark:text-slate-400`}>
                  Doctor
                </th>
              ) : null}
              <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>
                Total
              </th>
              <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>
                Discount
              </th>
              <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>
                Cash
              </th>
              <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>
                Card
              </th>
              <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>
                Bank
              </th>
              <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>
                Paid
              </th>
              <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white dark:divide-slate-700/80 dark:bg-slate-800">
            {displayedPayments.length === 0 ? (
              <tr>
                <td
                  colSpan={tableColCount}
                  className="px-2.5 py-6 text-center text-xs text-slate-500 dark:text-slate-400"
                >
                  No payments found.
                </td>
              </tr>
            ) : (
              displayedPayments.map((item: any, index) => {
                const amounts = resolveInvoiceAmounts(item);
                const apptTime = formatLinkedAppointmentTime(
                  item?.linkedAppointment
                );
                const apptToken = formatLinkedAppointmentToken(
                  item?.linkedAppointment
                );
                const apptPurpose = formatLinkedAppointmentPurpose(
                  item?.linkedAppointment
                );

                return (
                  <React.Fragment key={item?._id ?? index}>
                    <tr className="group transition-colors even:bg-slate-50/40 hover:bg-indigo-50/40 dark:even:bg-slate-800/60 dark:hover:bg-indigo-950/20">
                      <td className={`${cellClass} whitespace-nowrap`}>
                        <div className="font-semibold text-slate-800 dark:text-slate-100">
                          {listRowDate(item)}
                        </div>
                        {showApptTime && apptTime ? (
                          <div className="text-[11px] text-slate-600 dark:text-slate-300">
                            {apptTime}
                          </div>
                        ) : null}
                        <div className="text-[10px] text-slate-400 dark:text-slate-500">
                          #{item?.invoiceNo ?? "—"}
                        </div>
                      </td>
                      {showTokenColumn ? (
                        <td className={`${cellClass} text-center`}>
                          <span className="text-2xl font-bold tabular-nums leading-none tracking-tight text-slate-900 dark:text-slate-50">
                            {apptToken || "—"}
                          </span>
                        </td>
                      ) : null}
                      <td
                        className={`${cellClass} truncate text-slate-600 dark:text-slate-300`}
                        title={patientRegNo(item) || undefined}
                      >
                        {patientRegNo(item) || "—"}
                      </td>
                      <td className={cellClass}>
                        <div className="flex min-w-0 items-center gap-1.5">
                          <span
                            className="min-w-0 truncate font-semibold text-slate-900 dark:text-slate-100"
                            title={item?.fullName}
                          >
                            {item?.fullName || "—"}
                          </span>
                          {item?._id && item?.hasDrugs ? (
                            <PrescriptionButton
                              onClick={() => openDrugHistory(item)}
                            />
                          ) : null}
                        </div>
                      </td>
                      <td
                        className={`${cellClass} truncate text-slate-600 dark:text-slate-300`}
                        title={patientPhone(item) || undefined}
                      >
                        {patientPhone(item) || "—"}
                      </td>
                      {showPurposeColumn ? (
                        <td
                          className={`${cellClass} truncate text-slate-600 dark:text-slate-300`}
                          title={apptPurpose || undefined}
                        >
                          {apptPurpose || "—"}
                        </td>
                      ) : null}
                      {showDentistColumn ? (
                        <td
                          className={`${cellClass} truncate text-slate-600 dark:text-slate-300`}
                          title={dentistRowLabel(item)}
                        >
                          {dentistRowLabel(item)}
                        </td>
                      ) : null}
                      <td className={`${cellClass} whitespace-nowrap text-right font-medium tabular-nums text-slate-700 dark:text-slate-200`}>
                        {formatAmount(item?.totalPrice)}
                      </td>
                      <td className={`${cellClass} whitespace-nowrap text-right font-semibold tabular-nums text-orange-600 dark:text-orange-400`}>
                        {formatAmount(amounts.discount)}
                      </td>
                      <td className={`${cellClass} whitespace-nowrap text-right font-semibold tabular-nums text-emerald-600 dark:text-emerald-400`}>
                        {formatAmount(amounts.cash)}
                      </td>
                      <td className={`${cellClass} whitespace-nowrap text-right font-semibold tabular-nums text-indigo-600 dark:text-indigo-400`}>
                        {formatAmount(amounts.card)}
                      </td>
                      <td className={`${cellClass} whitespace-nowrap text-right font-semibold tabular-nums text-sky-600 dark:text-sky-400`}>
                        {formatAmount(amounts.bankTransfer)}
                      </td>
                      <td className={`${cellClass} whitespace-nowrap text-right font-semibold tabular-nums text-emerald-700 dark:text-emerald-300`}>
                        {formatAmount(amounts.paid)}
                      </td>
                      <td className={cellClass}>
                        <InvoiceRowActions
                          treatmentId={item?._id}
                          onViewTreatment={openTreatment}
                          onViewInvoice={openInvoice}
                        />
                      </td>
                    </tr>
                    {openpayments &&
                      item?.paymentInfo?.map((sub: any, subIndex: number) => {
                        const subPaid = getPaidTotal(sub);
                        const subTotal =
                          toAmount(sub?.payable) ||
                          toAmount(sub?.netotal) + toAmount(sub?.discount);
                        return (
                          <tr
                            key={`${item?._id}-${subIndex}`}
                            className="bg-slate-50/60 dark:bg-slate-700/40"
                          >
                            <td className={`${cellClass} pl-4`}>
                              <div className="text-[11px] font-medium text-slate-600 dark:text-slate-300">
                                #{sub?.invoiceNo ?? "—"}
                              </div>
                              <div className="text-[10px] text-slate-500 dark:text-slate-400">
                                {listRowDate(sub)}
                              </div>
                            </td>
                            {showTokenColumn ? <td className={cellClass} /> : null}
                            <td className={cellClass} />
                            <td className={cellClass} />
                            <td className={cellClass} />
                            {showPurposeColumn ? <td className={cellClass} /> : null}
                            {showDentistColumn ? <td className={cellClass} /> : null}
                            <td className={`${cellClass} whitespace-nowrap text-right tabular-nums text-slate-600 dark:text-slate-300`}>
                              {formatAmount(subTotal)}
                            </td>
                            <td className={`${cellClass} whitespace-nowrap text-right tabular-nums text-orange-600 dark:text-orange-400`}>
                              {formatAmount(sub?.discount)}
                            </td>
                            <td className={`${cellClass} whitespace-nowrap text-right tabular-nums text-emerald-600 dark:text-emerald-400`}>
                              {formatAmount(sub?.cash)}
                            </td>
                            <td className={`${cellClass} whitespace-nowrap text-right tabular-nums text-indigo-600 dark:text-indigo-400`}>
                              {formatAmount(sub?.card)}
                            </td>
                            <td className={`${cellClass} whitespace-nowrap text-right tabular-nums text-sky-600 dark:text-sky-400`}>
                              {formatAmount(sub?.bankTransfer)}
                            </td>
                            <td className={`${cellClass} whitespace-nowrap text-right tabular-nums text-emerald-700 dark:text-emerald-300`}>
                              {formatAmount(subPaid)}
                            </td>
                            <td className={cellClass} />
                          </tr>
                        );
                      })}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
          {todaySummary && (
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-slate-100/90 dark:border-slate-500 dark:bg-slate-700/70">
                <td
                  className={`${cellClass} font-bold text-slate-800 dark:text-slate-100`}
                  colSpan={summaryLabelColSpan}
                >
                  {summaryTitle}
                  <span className="ml-1.5 text-[11px] font-medium text-slate-500 dark:text-slate-400">
                    ({todaySummary.count} invoice
                    {todaySummary.count === 1 ? "" : "s"})
                  </span>
                </td>
                <td className={`${cellClass} whitespace-nowrap text-right font-bold tabular-nums text-slate-800 dark:text-slate-100`}>
                  {formatAmount(todaySummary.totalPrice)}
                </td>
                <td className={`${cellClass} whitespace-nowrap text-right font-bold tabular-nums text-orange-600 dark:text-orange-400`}>
                  {formatAmount(todaySummary.discount)}
                </td>
                <td className={`${cellClass} whitespace-nowrap text-right font-bold tabular-nums text-emerald-600 dark:text-emerald-400`}>
                  {formatAmount(todaySummary.cash)}
                </td>
                <td className={`${cellClass} whitespace-nowrap text-right font-bold tabular-nums text-indigo-600 dark:text-indigo-400`}>
                  {formatAmount(todaySummary.card)}
                </td>
                <td className={`${cellClass} whitespace-nowrap text-right font-bold tabular-nums text-sky-600 dark:text-sky-400`}>
                  {formatAmount(todaySummary.bankTransfer)}
                </td>
                <td className={`${cellClass} whitespace-nowrap text-right font-bold tabular-nums text-emerald-700 dark:text-emerald-300`}>
                  {formatAmount(todaySummary.paid)}
                </td>
                <td className={cellClass} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {/* Mobile Cards */}
      <div
        className={`relative space-y-2 transition-opacity duration-200 md:hidden ${
          listLoading ? "pointer-events-none opacity-55" : "opacity-100"
        }`}
      >
        {listLoading ? (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center pt-10">
            <div className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-xs font-medium text-indigo-700 shadow-sm dark:bg-slate-800 dark:text-indigo-300">
              <CircularProgress size={16} thickness={5} color="inherit" />
              Loading invoices…
            </div>
          </div>
        ) : null}
        {displayedPayments.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 px-3 py-5 text-center text-xs text-slate-500 dark:border-slate-600 dark:text-slate-400">
            No payments found.
          </div>
        ) : (
          displayedPayments.map((item: any, index) => {
            const amounts = resolveInvoiceAmounts(item);
            const apptTime = formatLinkedAppointmentTime(
              item?.linkedAppointment
            );
            const apptToken = formatLinkedAppointmentToken(
              item?.linkedAppointment
            );
            const apptPurpose = formatLinkedAppointmentPurpose(
              item?.linkedAppointment
            );

            return (
              <div
                key={item?._id ?? index}
                className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 dark:border-slate-600 dark:bg-slate-800/80"
              >
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-slate-800 dark:text-slate-100">
                      {listRowDate(item)}
                      {showApptTime && apptTime ? ` · ${apptTime}` : ""}
                    </p>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500">
                      #{item?.invoiceNo ?? "—"}
                    </p>
                    <div className="mt-1 flex min-w-0 items-center gap-1.5">
                      <p className="min-w-0 truncate text-xs font-semibold text-slate-900 dark:text-slate-100">
                        {item?.fullName || "—"}
                      </p>
                      {item?._id && item?.hasDrugs ? (
                        <PrescriptionButton
                          onClick={() => openDrugHistory(item)}
                        />
                      ) : null}
                    </div>
                    <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">
                      {patientRegNo(item) ? `Reg ${patientRegNo(item)}` : "Reg —"}
                      {" · "}
                      {patientPhone(item) || "—"}
                    </p>
                    {showTokenColumn ? (
                      <p className="mt-1 text-2xl font-bold tabular-nums leading-none tracking-tight text-slate-900 dark:text-slate-50">
                        {apptToken || "—"}
                      </p>
                    ) : null}
                    {showPurposeColumn ? (
                      <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">
                        {apptPurpose || "—"}
                      </p>
                    ) : null}
                    {showDentistColumn ? (
                      <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">
                        {dentistRowLabel(item)}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1.5 text-[11px] sm:grid-cols-4">
                  <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-700">
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">Total</p>
                    <p className="font-semibold tabular-nums text-slate-800 dark:text-slate-200">
                      {formatAmount(item?.totalPrice)}
                    </p>
                  </div>
                  <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-700">
                    <p className="text-[10px] text-emerald-600 dark:text-emerald-400">Paid</p>
                    <p className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">
                      {formatAmount(amounts.paid)}
                    </p>
                  </div>
                  <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-700">
                    <p className="text-[10px] text-orange-600 dark:text-orange-400">Discount</p>
                    <p className="font-semibold tabular-nums text-orange-700 dark:text-orange-300">
                      {formatAmount(amounts.discount)}
                    </p>
                  </div>
                  <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-700">
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">
                      Purpose
                    </p>
                    <p className="font-semibold text-slate-700 dark:text-slate-300 truncate">
                      {apptPurpose || "—"}
                    </p>
                  </div>
                </div>
                <div className="mt-2 flex justify-end border-t border-slate-200/80 pt-2 dark:border-slate-600/80">
                  <InvoiceRowActions
                    treatmentId={item?._id}
                    onViewTreatment={openTreatment}
                    onViewInvoice={openInvoice}
                  />
                </div>
              </div>
            );
          })
        )}
        {todaySummary && (
          <div className="rounded-lg border border-slate-300 bg-slate-100 p-2.5 dark:border-slate-500 dark:bg-slate-700/70">
            <p className="mb-2 text-xs font-bold text-slate-800 dark:text-slate-100">
              {summaryTitle}
              <span className="ml-1.5 font-medium text-slate-500 dark:text-slate-400">
                ({todaySummary.count} invoice
                {todaySummary.count === 1 ? "" : "s"})
              </span>
            </p>
            <div className="grid grid-cols-2 gap-1.5 text-[11px] sm:grid-cols-4">
              <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-800">
                <p className="text-[10px] text-slate-500 dark:text-slate-400">Total</p>
                <p className="font-bold tabular-nums text-slate-800 dark:text-slate-100">
                  {formatAmount(todaySummary.totalPrice)}
                </p>
              </div>
              <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-800">
                <p className="text-[10px] text-emerald-600 dark:text-emerald-400">Paid</p>
                <p className="font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
                  {formatAmount(todaySummary.paid)}
                </p>
              </div>
              <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-800">
                <p className="text-[10px] text-orange-600 dark:text-orange-400">Discount</p>
                <p className="font-bold tabular-nums text-orange-700 dark:text-orange-300">
                  {formatAmount(todaySummary.discount)}
                </p>
              </div>
              <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-800">
                <p className="text-[10px] text-indigo-600 dark:text-indigo-400">Card</p>
                <p className="font-bold tabular-nums text-indigo-700 dark:text-indigo-300">
                  {formatAmount(todaySummary.card)}
                </p>
              </div>
              <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-800">
                <p className="text-[10px] text-emerald-600 dark:text-emerald-400">Cash</p>
                <p className="font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
                  {formatAmount(todaySummary.cash)}
                </p>
              </div>
              <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-800">
                <p className="text-[10px] text-sky-600 dark:text-sky-400">Bank</p>
                <p className="font-bold tabular-nums text-sky-700 dark:text-sky-300">
                  {formatAmount(todaySummary.bankTransfer)}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      {!filtersLoadAllRows ? (
        <div
          className={`transition-opacity duration-200 ${
            listLoading ? "pointer-events-none opacity-55" : "opacity-100"
          }`}
        >
          <BottomTab
            title="Invoices"
            count={count}
            pageSize={INVOICE_LIST_PAGE_SIZE}
            onPageChange={onPageChange}
          />
        </div>
      ) : null}
      {report && <AllPaymentReport closeModal={closeModal} />}
      {drugModal && (
        <DrugHistoryModal
          treatmentId={drugModal.treatmentId}
          patientName={drugModal.patientName}
          invoiceNo={drugModal.invoiceNo}
          onClose={closeDrugModal}
        />
      )}
    </div>
  );
}

export default AllLastPayments;
